/**
 * Firm Success presentational blocks (#676) — the ratified mockup's
 * builders (fs-page-676.html) as React components over the API payloads.
 * Pure presentation: every number arrives computed from the worker, which
 * reads the live book (ruled session 06 — the atom split is gone); nothing
 * here does arithmetic beyond layout math (axis steps, intensity quantiles,
 * pie stops) and the two display rules: a share beside a count (pctNote) and
 * whether one value fills a pie, map or calendar (#1267).
 *
 * Ruled visual grammar carried from the record:
 *   - color is identity (case types, stages, section accents), never
 *     judgment; counts print beside every percentage; every windowed block
 *     declares its window; days that haven't happened are never drawn.
 *   - calendar intensity scales to the firm's OWN busiest day (the legend
 *     says so) — the standing per-firm-scaling ruling.
 */

import { useLayoutEffect, useRef, useState } from "react";
import type {
  FsCalendarWeek,
  FsCaseRow,
  FsClockRow,
  FsDailySeries,
  FsMapEntry,
  FsStatusCard,
} from "@command-center/shared";
import { US_MAP_VIEWBOX, US_STATE_PATHS } from "@/assets/us-states-paths";
import { US_STATE_NAMES } from "./fs-state-names";

// ---------------------------------------------------------------------------
// Identity palettes (ported from the ratified page; PI split adds a second
// distinguishable hue — magenta vs the PI purple — for the transfers bucket)
// ---------------------------------------------------------------------------

const CT_COLORS: Record<string, string> = {
  "Employment law": "hsl(224 72% 52%)",
  "PI leads": "hsl(268 60% 55%)",
  "PI transfers": "hsl(330 60% 45%)",
  "Workers' comp": "hsl(40 85% 42%)",
  "Med mal": "hsl(16 80% 50%)",
};
const CT_FALLBACK = "hsl(220 15% 62%)";

function ctColor(bucket: string): string {
  return CT_COLORS[bucket] ?? CT_FALLBACK;
}

const STAGE_COLORS: Record<string, string> = {
  "Under Review": "hsl(268 60% 58%)",
  Accepted: "hsl(190 75% 40%)",
  Placed: "hsl(174 70% 33%)",
  Settled: "hsl(150 55% 38%)",
  Closed: "hsl(230 30% 45%)",
  Dropped: "hsl(330 55% 52%)",
  Rejected: "hsl(220 12% 55%)",
  Declined: "hsl(20 60% 45%)",
};

function stageColor(stage: string): string {
  return STAGE_COLORS[stage] ?? "hsl(230 20% 40%)";
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

export function fmtDate(iso: string): string {
  const m = Number(iso.slice(5, 7));
  const d = Number(iso.slice(8, 10));
  return `${MONTHS[m - 1]} ${d}`;
}

/** Percentage beside counts, only where the denominator can carry one —
 *  the tiny-denominator ruling (no bare % under 10). */
function pctNote(a: number, b: number): string | null {
  if (b < 10) return null;
  return `(${Math.round((a / b) * 100)}%)`;
}

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------

export function QCard({
  title,
  accent,
  win,
  children,
  right,
}: {
  title: string;
  accent: string;
  win?: React.ReactNode;
  children: React.ReactNode;
  right?: React.ReactNode;
}) {
  return (
    <div className="q" style={{ "--accent": accent } as React.CSSProperties}>
      <h2>
        {title}
        {right}
      </h2>
      {win !== undefined ? <div className="win">{win}</div> : null}
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 90-day daily graph (R7 axis rules; R10 breakdown; R13 reached-Placed line)
// ---------------------------------------------------------------------------

function axisFor(maxDay: number): { step: number; top: number } {
  const step = Math.max(1, Math.ceil(maxDay / 5));
  let top = Math.ceil(maxDay * 1.3);
  top = Math.max(step, Math.ceil(top / step) * step);
  return { step, top };
}

export function DailyGraph({
  series,
  converted,
  convertedLabel,
  totalLabel,
}: {
  series: FsDailySeries;
  converted?: { date: string; count: number }[];
  convertedLabel?: string;
  totalLabel: string;
}) {
  const [mode, setMode] = useState<"total" | "types">("total");
  const points = series.points;
  const N = points.length;
  if (N === 0) return null;

  const { step, top } = axisFor(Math.max(1, series.busiestDay));
  const W = 920;
  const H = 250;
  const L = 38;
  const R = 12;
  const T = 12;
  const B = 34;
  const iw = W - L - R;
  const ih = H - T - B;
  const x = (i: number) => L + (N === 1 ? iw / 2 : i * (iw / (N - 1)));
  const y = (v: number) => T + ih - (v / top) * ih;

  const typeTotals = new Map<string, number>();
  for (const p of points) {
    for (const [t, n] of Object.entries(p.byType)) {
      typeTotals.set(t, (typeTotals.get(t) ?? 0) + n);
    }
  }
  const types = [...typeTotals.entries()].sort((a, b) => b[1] - a[1]);
  const windowTotal = points.reduce((s, p) => s + p.total, 0);
  const convertedTotal = (converted ?? []).reduce((s, p) => s + p.count, 0);

  const gridlines = [];
  for (let v = 0; v <= top; v += step) {
    gridlines.push(
      <g key={`y${v}`}>
        <line x1={L} y1={y(v)} x2={W - R} y2={y(v)} stroke="hsl(38 30% 90%)" strokeWidth={1} />
        <text x={L - 6} y={y(v) + 3.5} textAnchor="end" fontSize={10} fill="hsl(230 20% 40%)">
          {v}
        </text>
      </g>,
    );
  }
  const ticks = [];
  for (let i = 0; i < N; i += 7) {
    ticks.push(
      <g key={`t${i}`}>
        <line x1={x(i)} y1={T} x2={x(i)} y2={T + ih} stroke="hsl(38 30% 92%)" strokeWidth={1} />
        <text x={x(i)} y={H - B + 16} textAnchor="middle" fontSize={9.5} fill="hsl(230 20% 40%)">
          {fmtDate(points[i]!.date)}
        </text>
      </g>,
    );
  }

  const line = (
    counts: (i: number) => number,
    color: string,
    label: string,
    dashed: boolean,
  ) => {
    let d = "";
    const dots = [];
    for (let i = 0; i < N; i++) {
      const v = counts(i);
      d += `${i === 0 ? "M" : "L"}${x(i).toFixed(1)} ${y(v).toFixed(1)} `;
      dots.push(
        <circle key={i} cx={x(i)} cy={y(v)} r={2.4} fill={color} opacity={v > 0 ? 1 : 0.35}>
          <title>{`${fmtDate(points[i]!.date)} — ${v} ${label}`}</title>
        </circle>,
      );
    }
    return (
      <g key={`${label}-${dashed ? "dash" : "solid"}`}>
        <path d={d} fill="none" stroke={color} strokeWidth={2} strokeDasharray={dashed ? "5 4" : undefined} />
        {dots}
      </g>
    );
  };


  const seriesLines =
    mode === "total"
      ? [line((i) => points[i]!.total, "hsl(16 85% 48%)", totalLabel, false)]
      : types.map(([t]) => line((i) => points[i]!.byType[t] ?? 0, ctColor(t), t, false));
  const convertedLine =
    converted !== undefined
      ? line((i) => converted[i]?.count ?? 0, "hsl(174 70% 30%)", convertedLabel ?? "reached Placed", true)
      : null;

  return (
    <>
      <span className="chips">
        <button aria-pressed={mode === "total"} onClick={() => setMode("total")} type="button">
          Total
        </button>
        <button aria-pressed={mode === "types"} onClick={() => setMode("types")} type="button">
          By case type
        </button>
      </span>
      <div className="graph">
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="daily counts, past 90 days">
          {gridlines}
          {ticks}
          <line x1={L} y1={T + ih} x2={W - R} y2={T + ih} stroke="hsl(230 20% 60%)" strokeWidth={1.2} />
          {seriesLines}
          {convertedLine}
        </svg>
      </div>
      <div className="glegend">
        <span className="soft">
          Axis to {top}, scaled to this firm's busiest day (<b>{series.busiestDay}</b>) — not
          comparable across firms.
        </span>
      </div>
      <div className="glegend">
        {mode === "total" ? (
          <span className="k">
            <i style={{ background: "hsl(16 85% 48%)" }} />
            Total {totalLabel} per day <b className="num">{windowTotal}</b>
          </span>
        ) : (
          types.map(([t, n]) => (
            <span className="k" key={t}>
              <i style={{ background: ctColor(t) }} />
              {t} <b className="num">{n}</b>
            </span>
          ))
        )}
        {converted !== undefined ? (
          <span className="k">
            <i style={{ background: "hsl(174 70% 30%)", height: 0, borderTop: "3px dashed hsl(174 70% 30%)" }} />
            {convertedLabel} <b className="num">{convertedTotal}</b>
          </span>
        ) : null}
      </div>
    </>
  );
}

/** The graph caption. Thin by rule (session 06) — the window and the axis, no
 *  more. */
export function graphWindowNote(series: FsDailySeries): string {
  return `Past 90 days, ${fmtDate(series.start)} – ${fmtDate(series.end)}, one point per day. Today isn't drawn until it's over, so the counts below can run one day ahead.`;
}

// ---------------------------------------------------------------------------
// Calendar (Mon–Sun weeks; per-firm intensity; honest nulls)
// ---------------------------------------------------------------------------

export function CalendarBlock({ weeks }: { weeks: FsCalendarWeek[] }) {
  const max = Math.max(
    1,
    ...weeks.flatMap((w) => w.days.map((d) => d.count ?? 0)),
  );
  const quantile = (n: number) => (n === 0 ? 0 : Math.ceil((n / max) * 5));
  return (
    <>
      <div className="cal">
        <span />
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <span className="h" key={d}>
            {d}
          </span>
        ))}
        <span className="h">week</span>
        {weeks.map((w) => (
          <CalendarRow key={w.label} week={w} quantile={quantile} />
        ))}
      </div>
      <div className="callegend">
        Darker = more, against this firm's busiest day (<b>{max}</b>). The
        dashed cell is today. Every cell shows its count.
      </div>
    </>
  );
}

function CalendarRow({
  week,
  quantile,
}: {
  week: FsCalendarWeek;
  quantile: (n: number) => number;
}) {
  return (
    <>
      <span className="wl">{week.label}</span>
      {week.days.map((d) => {
        // Live-sourced (session 06): null now means only today or a day that
        // hasn't happened. Every past day carries a real count, so the grid
        // reads as a full grid.
        if (d.count === null) {
          const why = d.today ? "today, still counting" : "hasn't happened yet";
          return (
            <span key={d.date} className={`d blank${d.today ? " today" : ""}`} title={`${d.date} · ${why}`} />
          );
        }
        const q = quantile(d.count);
        return (
          <span key={d.date} className={`d ${d.count === 0 ? "n0" : `c${q}`}`} title={d.date}>
            {d.count}
          </span>
        );
      })}
      <span className="wt num">{week.total}</span>
    </>
  );
}

// ---------------------------------------------------------------------------
// Status cards + case list
// ---------------------------------------------------------------------------

export function StatusCardsRow({
  cards,
  cases,
  open,
  onToggle,
  dateHeading,
  showExactStatus,
  shareOf,
}: {
  cards: FsStatusCard[];
  cases: Record<string, FsCaseRow[]>;
  open: string | null;
  onToggle: (key: string | null) => void;
  dateHeading: string;
  showExactStatus: boolean;
  /** One card that prints its share of a total beside its count (#1267: the
   *  OF Rejected card, of all the Firm's Leads) — pctNote's rule. */
  shareOf?: { key: string; total: number };
}) {
  const openRows = open !== null ? (cases[open] ?? []) : [];
  return (
    <>
      <div className="cards">
        {cards.map((c) => (
          <button
            key={c.key}
            type="button"
            className="scard"
            style={{ "--sc": stageColor(c.key) } as React.CSSProperties}
            data-zero={c.count === 0 ? true : undefined}
            aria-pressed={open === c.key}
            onClick={() => {
              if (c.count > 0) onToggle(open === c.key ? null : c.key);
            }}
          >
            <b className="num">{c.count}</b>
            <span>{c.label}</span>
            {c.note !== null ? <small>{c.note}</small> : null}
            {shareOf !== undefined && shareOf.key === c.key && pctNote(c.count, shareOf.total) !== null ? (
              <small data-testid="fs-card-share">
                of {shareOf.total} {pctNote(c.count, shareOf.total)}
              </small>
            ) : null}
          </button>
        ))}
      </div>
      {open !== null && openRows.length > 0 ? (
        <div className="cardlist">
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Case type</th>
                <th>State</th>
                <th className="r">{dateHeading}</th>
                {showExactStatus ? <th className="r">Exact status</th> : null}
              </tr>
            </thead>
            <tbody>
              {openRows.map((row, i) => (
                <tr key={i}>
                  <td>{row.leadName}</td>
                  <td>{row.caseType}</td>
                  <td>{row.state ?? "—"}</td>
                  <td className="r num">{row.date !== null ? fmtDate(row.date) : "—"}</td>
                  {showExactStatus ? <td className="r">{row.exactStatus ?? "—"}</td> : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// The two time clocks (medians; n beside every figure)
// ---------------------------------------------------------------------------

function med1(days: number | null): string {
  if (days === null) return "—";
  return `${Math.round(days * 10) / 10} d`;
}

export function ClockTable({
  title,
  clockNote,
  rows,
}: {
  title: string;
  clockNote: string;
  rows: FsClockRow[];
}) {
  // The Firm against its own months, inside the 90-day window (#1267): the
  // 90-day median first, then the window's three 30-day blocks, newest first.
  return (
    <div>
      <h3>
        {title} <div className="clock">{clockNote}</div>
      </h3>
      <table>
        <thead>
          <tr>
            <th>Case type</th>
            <th className="r">Last 90 days (n)</th>
            <th className="r">Last 30 days</th>
            <th className="r">30–60 days ago</th>
            <th className="r">60–90 days ago</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.bucket ?? "__overall"}>
              <td>{r.bucket === null ? <b>Overall</b> : r.bucket}</td>
              <td className="r num">
                {med1(r.mineMedianDays)} <span className="soft">({r.mineN})</span>
              </td>
              {r.byThirtyDays.map((b, i) => (
                <td key={i} className="r num">
                  {med1(b.medianDays)} <span className="soft">({b.n})</span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Type mix pie
// ---------------------------------------------------------------------------

export function MixPie({
  mix,
  total,
}: {
  mix: { bucket: string; count: number }[];
  total: number;
}) {
  if (total === 0) return <div className="soft">No recorded leads yet.</div>;
  let acc = 0;
  const stops = mix.map((m) => {
    const a = (acc / total) * 360;
    acc += m.count;
    const b = (acc / total) * 360;
    return `${ctColor(m.bucket)} ${a}deg ${b}deg`;
  });
  return (
    <div className="pie">
      <div className="disc" style={{ background: `conic-gradient(${stops.join(",")})` }} />
      <ul>
        {mix.map((m) => (
          <li key={m.bucket}>
            <i style={{ background: ctColor(m.bucket) }} />
            {m.bucket}
            <b className="num">{m.count}</b>
          </li>
        ))}
        <li className="total">
          Total<b className="num">{total}</b>
        </li>
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One value fills it — a sentence instead of a chart (#1267)
// ---------------------------------------------------------------------------

/** A pie one bucket fills this much of draws a near-solid disc that answers
 *  nothing a sentence does not; it is written as a line instead. 90% is a
 *  judgment (Claude, engineering, #1267 s01): the measured case was 98.7%
 *  (Barrett & Farahany, Employment law 314 of 318, 2026-10-17). */
const ONE_BUCKET_SHARE = 0.9;

/** The mix as one line when one bucket fills it, else null (draw the pie).
 *  Every bucket's count stays on the line. */
export function mixAsLine(
  mix: { bucket: string; count: number }[],
  total: number,
): string | null {
  if (total === 0) return null;
  const sorted = [...mix].filter((m) => m.count > 0).sort((a, b) => b.count - a.count);
  const top = sorted[0];
  if (top === undefined || top.count / total < ONE_BUCKET_SHARE) return null;
  const rest = sorted.slice(1).map((m) => `${m.bucket} ${m.count}`);
  return [`${top.bucket}, ${top.count} of ${total}`, ...rest].join(" · ") + ".";
}

/** The map as one line when it shades one state or none, else null (draw the
 *  map). The no-state count stays on the line so the total still foots. */
export function mapAsLine(map: FsMapEntry[], noState: number): string | null {
  const shaded = map.filter((e) => e.total > 0);
  if (shaded.length > 1) return null;
  const noStateText = noState > 0 ? `${noState} with no state recorded` : null;
  const only = shaded[0];
  if (only === undefined) return noStateText !== null ? `${noStateText}.` : "None yet.";
  const name = US_STATE_NAMES[only.state] ?? only.state;
  const where = `${only.total} in ${name} (${only.state})`;
  return noStateText !== null ? `${where}, and ${noStateText}.` : `All ${where}.`;
}

/** True when every finished day of the five weeks counted zero. */
export function calendarAllZero(weeks: FsCalendarWeek[]): boolean {
  return weeks.every((w) => w.days.every((d) => d.count === null || d.count === 0));
}

/** The empty calendar as one sentence: what did not happen in these five
 *  weeks, and the last day the 90-day series counted one. */
export function emptyCalendarLine(noun: string, series: FsDailySeries): string {
  const days = series.points.filter((p) => p.total > 0).map((p) => p.date);
  const last = days.length > 0 ? days.reduce((a, b) => (a > b ? a : b)) : null;
  return last !== null
    ? `${noun} in these five weeks; the last was ${fmtDate(last)}.`
    : `${noun} in these five weeks, nor in the past 90 days.`;
}

// ---------------------------------------------------------------------------
// Firm-scoped choropleth (CC's coverage-map form; counts printed on states)
// ---------------------------------------------------------------------------

const MAP_RAMP = [
  "hsl(45 90% 88%)",
  "hsl(42 92% 74%)",
  "hsl(38 92% 58%)",
  "hsl(30 90% 48%)",
  "hsl(22 88% 38%)",
] as const;

export function FsMapBlock({
  map,
  noState,
  total,
  sideTitle,
  chips,
}: {
  map: FsMapEntry[];
  noState: number;
  total: number;
  sideTitle: string;
  chips: boolean;
}) {
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  // R17's third layer: click a state for its exact per-type counts. The build
  // shipped without it (the stylesheet still carried the `.sel` rule and a
  // pointer cursor for a click that did nothing) — restored session 06.
  const [selected, setSelected] = useState<string | null>(null);
  // Counts print ON the shaded states (R17 as accepted — the mockup's
  // labelMap pass). Anchors come from each path's measured bounding box
  // after mount; states whose drawn box is under ~30px stay unlabeled and
  // keep the hover title + side table.
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [anchors, setAnchors] = useState<Record<string, [number, number]>>({});
  useLayoutEffect(() => {
    const svg = svgRef.current;
    if (svg === null) return;
    const next: Record<string, [number, number]> = {};
    for (const path of svg.querySelectorAll<SVGPathElement>("path[data-state]")) {
      const code = path.dataset.state;
      if (code === undefined) continue;
      const box = path.getBBox();
      if (box.width < 30 || box.height < 22) continue;
      next[code] = [box.x + box.width / 2, box.y + box.height / 2];
    }
    setAnchors(next);
  }, []);
  const allTypes = [...new Set(map.flatMap((e) => Object.keys(e.byType)))].sort();
  const countFor = (e: FsMapEntry) =>
    typeFilter === null ? e.total : (e.byType[typeFilter] ?? 0);
  const max = Math.max(1, ...map.map(countFor));
  const byState = new Map(map.map((e) => [e.state, e]));

  const fillFor = (code: string): string => {
    const e = byState.get(code);
    const n = e !== undefined ? countFor(e) : 0;
    if (n === 0) return "hsl(40 20% 93%)";
    const idx = Math.min(4, Math.ceil((n / max) * 5) - 1);
    return MAP_RAMP[idx]!;
  };
  const inkFor = (code: string): string => {
    const e = byState.get(code);
    const n = e !== undefined ? countFor(e) : 0;
    return n / max > 0.5 ? "#fff" : "hsl(230 45% 15%)";
  };

  return (
    <>
      {chips && allTypes.length > 1 ? (
        <span className="chips" style={{ marginBottom: 10, display: "inline-flex" }}>
          <button type="button" aria-pressed={typeFilter === null} onClick={() => setTypeFilter(null)}>
            Total
          </button>
          {allTypes.map((t) => (
            <button
              key={t}
              type="button"
              aria-pressed={typeFilter === t}
              onClick={() => setTypeFilter(typeFilter === t ? null : t)}
              style={{ "--cc": ctColor(t) } as React.CSSProperties}
            >
              <i />
              {t}
            </button>
          ))}
        </span>
      ) : null}
      <div className="maprow">
        <div className="map">
          <svg
            ref={svgRef}
            viewBox={US_MAP_VIEWBOX}
            role="img"
            aria-label="firm map by state"
          >
            {Object.entries(US_STATE_PATHS).map(([code, d]) => (
              <path
                key={code}
                d={d}
                data-state={code}
                className={selected === code ? "sel" : undefined}
                style={
                  (byState.get(code) !== undefined ? countFor(byState.get(code)!) : 0) === 0
                    ? { cursor: "default" }
                    : undefined
                }
                fill={fillFor(code)}
                stroke="#fff"
                strokeWidth={1}
                onClick={() => {
                  const e = byState.get(code);
                  const n = e !== undefined ? countFor(e) : 0;
                  // A state with no cases has no breakdown to show.
                  if (n === 0) return;
                  setSelected(selected === code ? null : code);
                }}
              >
                <title>
                  {US_STATE_NAMES[code] ?? code}:{" "}
                  {byState.get(code) !== undefined ? countFor(byState.get(code)!) : 0}
                </title>
              </path>
            ))}
            {map.map((e) => {
              const anchor = anchors[e.state];
              const n = countFor(e);
              if (anchor === undefined || n === 0) return null;
              return (
                <text
                  key={e.state}
                  x={anchor[0]}
                  y={anchor[1]}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={13}
                  fill={inkFor(e.state)}
                >
                  {n}
                </text>
              );
            })}
          </svg>
        </div>
        {selected !== null ? (
          <div className="mapside">
            <h3>
              {US_STATE_NAMES[selected] ?? selected} —{" "}
              {byState.get(selected) !== undefined ? byState.get(selected)!.total : 0} total
            </h3>
            <table>
              <tbody>
                {Object.entries(byState.get(selected)?.byType ?? {})
                  .sort((a, b) => b[1] - a[1])
                  .map(([t, n]) => (
                    <tr key={t}>
                      <td className="stage" style={{ "--sc": ctColor(t) } as React.CSSProperties}>
                        <i />
                        {t}
                      </td>
                      <td className="r num">
                        <b>{n}</b>
                      </td>
                    </tr>
                  ))}
                {Object.keys(byState.get(selected)?.byType ?? {}).length === 0 ? (
                  <tr>
                    <td className="soft">none</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
            <div className="note">Click the state again to close.</div>
          </div>
        ) : (
        <div className="mapside">
          <h3>
            {sideTitle}
            {typeFilter !== null ? ` — ${typeFilter}` : ""}
          </h3>
          <table>
            <tbody>
              {map.slice(0, 12).map((e) => (
                <tr key={e.state}>
                  <td>{US_STATE_NAMES[e.state] ?? e.state}</td>
                  <td className="r num">{countFor(e)}</td>
                </tr>
              ))}
              {map.length > 12 ? (
                // The remainder row keeps the visible list summing to the
                // Total below it — a truncated list with no remainder reads
                // as a reconciliation failure (cold-read catch).
                <tr>
                  <td className="soft">{map.length - 12} more states</td>
                  <td className="r num soft">
                    {map.slice(12).reduce((s, e) => s + countFor(e), 0)}
                  </td>
                </tr>
              ) : null}
              <tr>
                <td>
                  <b>{typeFilter === null ? "Total" : `Total — ${typeFilter}`}</b>
                </td>
                <td className="r num">
                  <b>
                    {typeFilter === null
                      ? total - noState
                      : map.reduce((s, e) => s + countFor(e), 0)}
                  </b>
                </td>
              </tr>
              {typeFilter === null && noState > 0 ? (
                <tr>
                  <td className="soft">no state recorded</td>
                  <td className="r num soft">{noState}</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </>
  );
}

